import { IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { useTranslation } from '../../i18n/LocaleProvider';
import { PRESS } from '../../interaction';

export type StepStatus = 'upcoming' | 'current' | 'complete' | 'error' | 'disabled';
export type StepperOrientation = 'horizontal' | 'vertical';

export interface StepItem {
  label: ReactNode;
  description?: ReactNode;
  /** Overrides the status derived from `currentStep`. */
  status?: StepStatus;
}

export interface StepperProps extends Omit<ComponentPropsWithRef<'ol'>, 'children'> {
  steps: StepItem[];
  /** Zero-based. Everything before it reads as complete, everything after as upcoming. */
  currentStep?: number;
  orientation?: StepperOrientation;
  /** Makes reachable steps clickable. Needs `onStepClick` to do anything. */
  clickable?: boolean;
  onStepClick?: (index: number) => void;
  /** Accessible name for the step list. */
  label?: string;
}

const MARKER: Record<StepStatus, string> = {
  complete: 'border-primary bg-primary text-on-primary',
  current: 'border-primary bg-primary-subtle text-primary',
  upcoming: 'border-line bg-transparent text-muted',
  error: 'border-danger bg-danger text-on-primary',
  disabled: 'border-line-subtle bg-transparent text-muted opacity-60',
};

const TEXT: Record<StepStatus, string> = {
  complete: 'text-body',
  current: 'text-heading',
  upcoming: 'text-muted',
  error: 'text-danger',
  disabled: 'text-muted',
};

function statusOf(step: StepItem, index: number, currentStep: number): StepStatus {
  if (step.status) return step.status;
  if (index < currentStep) return 'complete';
  if (index === currentStep) return 'current';
  return 'upcoming';
}

/**
 * Numbered progress across steps.
 *
 * Horizontal geometry is the §34 gotcha's fixed one-row shape: each step is
 * `flex-1 min-w-0` so a long label wraps *inside* the step, and the connector is
 * a short `shrink-0` rule. Sizing the steps fixed and the connector wide is the
 * version that overflows the row.
 */
export function Stepper({
  steps,
  currentStep = 0,
  orientation = 'horizontal',
  clickable = false,
  onStepClick,
  label = 'Progress',
  className,
  ...rest
}: StepperProps) {
  const { t } = useTranslation();
  const current = steps[currentStep];
  const currentLabel = current && typeof current.label === 'string' ? current.label : undefined;
  const vertical = orientation === 'vertical';

  return (
    <div className="min-w-0">
      <ol
        {...rest}
        aria-label={label}
        className={cn(
          'list-none p-0',
          vertical ? 'flex flex-col' : 'flex min-w-0 flex-row items-start gap-y-3',
          className,
        )}
      >
        {steps.map((step, index) => {
          const status = statusOf(step, index, currentStep);
          const isLast = index === steps.length - 1;
          const canClick =
            clickable && Boolean(onStepClick) && status !== 'disabled' && status !== 'current';

          const marker = (
            <span
              aria-hidden="true"
              className={cn(
                'grid size-8 shrink-0 place-items-center rounded-pill border-2',
                // No `tabular-nums` — inert on `font-display` (Oswald has no
                // `tnum`); see the note beside `--font-display` in tokens.css.
                'font-display text-xs font-semibold',
                MARKER[status],
              )}
            >
              {status === 'complete' ? (
                <IconCheck className="size-4" stroke={3} />
              ) : status === 'error' ? (
                <IconAlertTriangle className="size-4" stroke={2.5} />
              ) : (
                index + 1
              )}
            </span>
          );

          const text = (
            <span className={cn('flex min-w-0 flex-col gap-y-1', vertical ? 'pb-6' : '')}>
              <span
                className={cn(
                  'min-w-0 font-display text-xs font-semibold tracking-label uppercase',
                  TEXT[status],
                )}
              >
                {step.label}
              </span>
              {step.description ? (
                <span className="min-w-0 text-xs text-muted">{step.description}</span>
              ) : null}
            </span>
          );

          const body =
            canClick ? (
              <button
                type="button"
                onClick={() => onStepClick?.(index)}
                className={cn(
                  // 44px floor, so the row never needs the `dhd-target` overlay.
                  'flex min-h-11 min-w-0 flex-1 items-start gap-x-2.5 rounded-xs text-left',
                  'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
                  'hover:bg-surface-muted',
                  PRESS,
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                )}
              >
                {marker}
                {text}
              </button>
            ) : (
              <span className="flex min-h-11 min-w-0 flex-1 items-start gap-x-2.5">
                {marker}
                {text}
              </span>
            );

          return (
            <li
              key={`step-${index}`}
              aria-current={status === 'current' ? 'step' : undefined}
              className={cn('min-w-0', vertical ? 'flex gap-x-3' : 'flex items-start gap-x-3', !vertical && !isLast && 'flex-1')}
            >
              {/* Ahead of the body so the position is heard before the label. */}
              <span className="dhd-sr-only">
                {t('ds.stepper.position', { current: index + 1, total: steps.length })}
              </span>

              {vertical ? (
                <>
                  <span className="flex shrink-0 flex-col items-center" aria-hidden="true">
                    {marker}
                    {!isLast ? <span className="w-px flex-1 bg-line" /> : null}
                  </span>
                  {canClick ? (
                    <button
                      type="button"
                      onClick={() => onStepClick?.(index)}
                      className={cn(
                        'flex min-h-11 min-w-0 flex-1 items-start rounded-xs text-left',
                        'transition-colors duration-[var(--dur-fast)]',
                        'hover:bg-surface-muted',
                        PRESS,
                        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                      )}
                    >
                      {text}
                    </button>
                  ) : (
                    text
                  )}
                </>
              ) : (
                <>
                  {body}
                  {!isLast ? (
                    <span
                      aria-hidden="true"
                      className="mt-4 h-px w-4 shrink-0 bg-line sm:w-6"
                    />
                  ) : null}
                </>
              )}
            </li>
          );
        })}
      </ol>

      {/* Announced on change, so a pointer-free user is told the step moved
          without having to walk the list again. */}
      <p role="status" aria-live="polite" className="dhd-sr-only">
        {`Step ${currentStep + 1} of ${steps.length}${currentLabel ? `: ${currentLabel}` : ''}`}
      </p>
    </div>
  );
}
